import { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tooltip,
  Row,
  Col,
  Card,
  App as AntApp
} from 'antd';
import {
  ReloadOutlined,
  UserAddOutlined,
  TeamOutlined,
  UserOutlined,
  LockOutlined,
  UnlockOutlined,
  StopOutlined,
  PlusOutlined,
  MailOutlined,
  PhoneOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import endpoints from '../../api/endpoints';
import { useQuery } from '../../hooks/useApi';
import {
  isSubUsername,
  passwordStrength,
  isEmail,
  isMobileIN,
  digitsOnly
} from '../../utils/validators';
import { isSubUserRole } from '../../utils/validation';
import {
  sanitizeText,
  sanitizeUsername,
  sanitizeEmail,
  sanitizeMobile
} from '../../utils/sanitize';
import { formatDateTime } from '../../utils/format';
import { useAppNotifications } from '../../utils/notify';
import useAuth from '../../hooks/useAuth';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingBlock from '../../components/LoadingBlock';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import UserDetailsPanel from './UserDetailsPanel';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'admin' },
  { value: 'manager', label: 'manager' },
  { value: 'staff', label: 'staff' },
  { value: 'viewer', label: 'viewer' }
];

// Backend returns status as one of active | locked | surrendered, but
// legacy rows may only carry isActive — fall through gracefully so the
// badge always has something to render.
function resolveStatus(row) {
  if (row.status) return row.status;
  if (row.isMaster) return 'master';
  return row.isActive ? 'active' : 'surrendered';
}

function initialsOf(name, email) {
  const src = (name || email || 'U').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

// Avatar tile for the User column. Color is deterministic so the same
// user always gets the same hue across renders.
const AVATAR_TONES = [
  'bg-brand-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-cyan-500',
  'bg-rose-500'
];
function toneFor(seed) {
  const s = String(seed || '');
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length];
}

export default function UserListPage() {
  const usersQ = useQuery(() => endpoints.users.list(), []);
  const subQ = useQuery(() => endpoints.subscription.get(), []);
  const { notifyError, flashMessage } = useAppNotifications();
  const { modal } = AntApp.useApp();
  const navigate = useNavigate();
  const { me } = useAuth();

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();
  // Stage 5L: details rendered in a popup modal — clicking a row or the
  // View link opens the modal; closing it returns the page to the table.
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const users = (usersQ.data && usersQ.data.users) || [];
  const sub = subQ.data;
  const seats = sub ? sub.users || sub.seats || null : null;
  const selectedUser = users.find((u) => u.id === selectedUserId) || null;
  const noSlotsLeft = seats ? seats.available <= 0 : false;

  function openDetails(userId) {
    setSelectedUserId(userId);
    setDetailsOpen(true);
  }
  function closeDetails() {
    setDetailsOpen(false);
  }

  // Keep the modal closed if the selected user disappears (e.g. after a
  // refetch where the row was removed).
  useEffect(() => {
    if (!selectedUser && detailsOpen) setDetailsOpen(false);
  }, [selectedUser, detailsOpen]);

  async function refreshAll() {
    await Promise.all([usersQ.refetch(), subQ.refetch()]);
  }

  // ---- lifecycle action handlers ----
  async function performLifecycle({ id, action, reason, successMsg }) {
    try {
      if (action === 'lock') await endpoints.users.lock(id, { reason });
      else if (action === 'unlock') await endpoints.users.unlock(id, { reason });
      else if (action === 'surrender') await endpoints.users.surrender(id, { reason });
      flashMessage(successMsg, 'success');
      await refreshAll();
    } catch (err) {
      notifyError(err, { title: 'Action failed' });
    }
  }

  function openLockModal(row) {
    let reasonValue = '';
    modal.confirm({
      title: 'Lock user access?',
      icon: <LockOutlined className="!text-orange-500" />,
      okText: 'Lock user',
      cancelText: 'Cancel',
      content: (
        <div className="mt-2">
          <p className="text-sm text-ink-700">
            This will block <strong>{row.email}</strong> from logging in. The
            user will still occupy a user slot in your current plan.
          </p>
          <div className="mt-3">
            <label className="text-xs font-medium text-ink-700 block mb-1">
              Reason (optional)
            </label>
            <Input.TextArea
              rows={3}
              maxLength={500}
              placeholder="e.g. Temporary leave"
              onChange={(e) => {
                reasonValue = e.target.value;
              }}
            />
          </div>
        </div>
      ),
      onOk: () =>
        performLifecycle({
          id: row.id,
          action: 'lock',
          reason: reasonValue ? reasonValue.trim() : undefined,
          successMsg: `Locked ${row.email}`
        })
    });
  }

  function openUnlockModal(row) {
    let reasonValue = '';
    modal.confirm({
      title: 'Unlock user access?',
      icon: <UnlockOutlined className="!text-emerald-500" />,
      okText: 'Unlock user',
      cancelText: 'Cancel',
      content: (
        <div className="mt-2">
          <p className="text-sm text-ink-700">
            <strong>{row.email}</strong> will be able to log in again.
          </p>
          <div className="mt-3">
            <label className="text-xs font-medium text-ink-700 block mb-1">
              Reason (optional)
            </label>
            <Input.TextArea
              rows={3}
              maxLength={500}
              placeholder="e.g. Returned from leave"
              onChange={(e) => {
                reasonValue = e.target.value;
              }}
            />
          </div>
        </div>
      ),
      onOk: () =>
        performLifecycle({
          id: row.id,
          action: 'unlock',
          reason: reasonValue ? reasonValue.trim() : undefined,
          successMsg: `Unlocked ${row.email}`
        })
    });
  }

  function openSurrenderModal(row) {
    let reasonValue = '';
    modal.confirm({
      title: 'Surrender this user?',
      icon: <StopOutlined className="!text-rose-500" />,
      okText: 'Surrender user',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      content: (
        <div className="mt-2">
          <p className="text-sm text-ink-700">
            This will disable <strong>{row.email}</strong> immediately and
            release one user slot. The user will be removed from billing for
            the next term.
          </p>
          <div className="mt-3">
            <label className="text-xs font-medium text-ink-700 block mb-1">
              Reason (optional)
            </label>
            <Input.TextArea
              rows={3}
              maxLength={500}
              placeholder="e.g. Left the company"
              onChange={(e) => {
                reasonValue = e.target.value;
              }}
            />
          </div>
        </div>
      ),
      onOk: () =>
        performLifecycle({
          id: row.id,
          action: 'surrender',
          reason: reasonValue ? reasonValue.trim() : undefined,
          successMsg: `Surrendered ${row.email}`
        })
    });
  }

  // Stage 5K-5D: the action menu was hiding lifecycle actions behind a
  // 3-dot dropdown that testers found unintuitive. Per spec, the table
  // now exposes a single "View" affordance and the drawer carries the
  // primary actions (Lock / Unlock / Reset password / Surrender) as
  // visible buttons. Row-click also opens the drawer.

  // ---- table columns ----
  const columns = [
    {
      title: 'User',
      key: 'user',
      render: (_v, row) => {
        const display = row.displayName || row.fullName || row.username || row.email;
        return (
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className={
                'flex h-9 w-9 flex-none items-center justify-center rounded-full text-white text-xs font-semibold ' +
                toneFor(row.id || row.email)
              }
            >
              {initialsOf(display, row.email)}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-ink-900 truncate">
                {display}
              </div>
              <div className="text-xs text-ink-500 truncate font-mono">
                {row.username || '—'}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      title: 'Login',
      dataIndex: 'email',
      responsive: ['md'],
      render: (v) => (
        <span className="text-sm text-ink-700 truncate inline-block max-w-[260px]">
          {v}
        </span>
      )
    },
    {
      title: 'Role',
      dataIndex: 'role',
      width: 120,
      render: (v, row) => (
        <StatusBadge status={row.isMaster ? 'master' : v || 'viewer'} />
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      render: (_v, row) => <StatusBadge status={resolveStatus(row)} />
    },
    {
      title: 'Activity',
      key: 'activity',
      responsive: ['lg'],
      render: (_v, row) => (
        <div className="text-xs leading-5">
          <div className="text-ink-700">
            <span className="text-ink-500">Last login:</span>{' '}
            {row.lastLoginAt ? formatDateTime(row.lastLoginAt) : '—'}
          </div>
          <div className="text-ink-500">
            Created: {formatDateTime(row.createdAt)}
          </div>
        </div>
      )
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      width: 80,
      render: (_v, row) => (
        // Stage 5L: View opens the details modal. Row click does the
        // same (handled below via onRow).
        <Button
          type="text"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            openDetails(row.id);
          }}
          className="!text-brand-600 hover:!text-brand-700 hover:!bg-brand-50 !rounded-md !font-medium"
        >
          View
        </Button>
      )
    }
  ];

  async function submitCreate(values) {
    setCreating(true);
    try {
      const role = isSubUserRole(values.role) ? values.role : null;
      if (!role) {
        notifyError(null, {
          title: 'Invalid role',
          description: 'Please pick admin, manager, staff, or viewer.'
        });
        return;
      }
      const displayName = values.displayName
        ? sanitizeText(values.displayName, {
            collapseWhitespace: true,
            maxLength: 120
          })
        : '';
      const body = {
        username: sanitizeUsername(values.username),
        password: values.password,
        role,
        displayName,
        // Send fullName too so the legacy column stays populated for any
        // older read paths that haven't migrated to displayName.
        fullName: displayName,
        profileEmail: values.profileEmail
          ? sanitizeEmail(values.profileEmail)
          : undefined,
        profileMobile: values.profileMobile
          ? sanitizeMobile(values.profileMobile)
          : undefined
      };
      const created = await endpoints.users.create(body);
      flashMessage(`Created ${created.email}`, 'success');
      setCreateOpen(false);
      form.resetFields();
      await refreshAll();
    } catch (err) {
      notifyError(err, { title: 'Could not create sub-user' });
    } finally {
      setCreating(false);
    }
  }

  function handleCreateClick() {
    // If the plan has zero free slots, surface the friendly modal
    // immediately instead of letting the create-form 422 round-trip
    // produce a confusing error after the user fills in 6 fields.
    if (noSlotsLeft) {
      modal.warning({
        title: 'No user slots available',
        content:
          'Your current plan has no available user slots. Add users from Subscription or surrender an unused user before creating a new login.',
        okText: 'OK'
      });
      return;
    }
    setCreateOpen(true);
  }

  // ---- header summary chips ----
  // Compact, professional pill-row that matches the rhythm of the
  // Subscription/Profile pages: subtle border + soft hover-free fill,
  // metric value bolder than its label.
  const seatChips = seats ? (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="inline-flex items-center gap-1.5 rounded-md border border-surface-200 bg-white px-2.5 py-1 text-[12px] text-ink-600">
        <strong className="text-ink-900 font-semibold">{seats.total}</strong>
        <span>slots</span>
      </span>
      <span className="inline-flex items-center gap-1.5 rounded-md border border-surface-200 bg-white px-2.5 py-1 text-[12px] text-ink-600">
        <strong className="text-ink-900 font-semibold">{seats.used}</strong>
        <span>used</span>
      </span>
      <span
        className={
          'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px] font-medium ' +
          (seats.available > 0
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-amber-200 bg-amber-50 text-amber-700')
        }
      >
        <strong className="font-semibold">{seats.available}</strong>
        <span>available</span>
      </span>
    </div>
  ) : null;

  return (
    <div>
      <PageHeader
        title="Users"
        subtitle="Create, manage, and review every login on your account. Each sub-user occupies one slot from your subscription."
        icon={<TeamOutlined />}
        actions={
          <>
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => refreshAll()}
                aria-label="Refresh"
                className="!h-9 !rounded-lg !font-medium"
              />
            </Tooltip>
            <Button
              icon={<PlusOutlined />}
              onClick={() => navigate('/subscription')}
              className="!h-9 !rounded-lg !font-medium"
            >
              Add users
            </Button>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleCreateClick}
              className="!h-9 !rounded-lg !font-medium !bg-[#1877F2] hover:!bg-blue-700 !border-0 !shadow-sm"
            >
              Create sub-user
            </Button>
          </>
        }
      />

      {seatChips ? (
        <div className="-mt-3 mb-5">{seatChips}</div>
      ) : null}

      <ErrorAlert error={usersQ.error} />
      <ErrorAlert error={subQ.error} />

      <Card styles={{ body: { padding: 0 } }}>
        {users.length === 0 && !usersQ.loading ? (
          <EmptyState
            icon={<TeamOutlined />}
            title="No sub-users yet"
            description="Create one with the button above. They'll log in at {username}@user.glass-i."
            action={
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={handleCreateClick}
              >
                Create sub-user
              </Button>
            }
          />
        ) : (
          <Table
            rowKey="id"
            dataSource={users}
            columns={columns}
            loading={usersQ.loading}
            pagination={false}
            size="small"
            tableLayout="auto"
            className="user-list-table"
            rowClassName={() => 'cursor-pointer user-list-row'}
            onRow={(row) => ({
              onClick: () => openDetails(row.id)
            })}
          />
        )}
      </Card>

      <Modal
        open={createOpen}
        title={
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-50 text-brand-500 text-lg">
              <UserAddOutlined />
            </span>
            <span className="font-semibold text-ink-900">Create sub-user</span>
          </div>
        }
        onCancel={() => setCreateOpen(false)}
        onOk={() => form.submit()}
        okText="Create"
        confirmLoading={creating}
        width={640}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={submitCreate}
          disabled={creating}
          size="middle"
          className="!mt-2"
          initialValues={{ role: 'manager' }}
        >
          <Form.Item
            label={<span className="text-sm font-medium text-ink-700">Username</span>}
            name="username"
            rules={[
              { required: true, message: 'username is required' },
              {
                validator: (_r, v) =>
                  !v || isSubUsername(String(v).toLowerCase())
                    ? Promise.resolve()
                    : Promise.reject(new Error('lowercase, dot, dash, underscore — 3-30 chars'))
              }
            ]}
            extra={
              <span className="text-xs text-ink-500">
                Login email will be {'{username}'}@user.glass-i
              </span>
            }
          >
            <Input prefix={<UserOutlined className="text-ink-400" />} placeholder="anita" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={14}>
              <Form.Item
                label={<span className="text-sm font-medium text-ink-700">Display name</span>}
                name="displayName"
                rules={[
                  { required: true, message: 'display name is required' },
                  { min: 2, max: 120, message: '2–120 characters' }
                ]}
                extra={
                  <span className="text-xs text-ink-500">
                    Shown in app screens and reports.
                  </span>
                }
              >
                <Input placeholder="Anita Sharma" />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                label={<span className="text-sm font-medium text-ink-700">Role</span>}
                name="role"
                rules={[{ required: true, message: 'role is required' }]}
              >
                <Select options={ROLE_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span className="text-sm font-medium text-ink-700">Mobile (optional)</span>}
                name="profileMobile"
                getValueFromEvent={digitsOnly(10)}
                rules={[
                  {
                    validator: (_r, v) =>
                      !v || isMobileIN(String(v))
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error('10-digit Indian mobile starting with 6-9')
                          )
                  }
                ]}
                extra={
                  <span className="text-xs text-ink-500">
                    Used as a contact detail only.
                  </span>
                }
              >
                <Input
                  prefix={<PhoneOutlined className="text-ink-400" />}
                  placeholder="9876543210"
                  maxLength={10}
                  inputMode="numeric"
                  autoComplete="tel"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<span className="text-sm font-medium text-ink-700">Email (optional)</span>}
                name="profileEmail"
                rules={[
                  {
                    validator: (_r, v) =>
                      !v || isEmail(String(v))
                        ? Promise.resolve()
                        : Promise.reject(new Error('enter a valid email'))
                  }
                ]}
                extra={
                  <span className="text-xs text-ink-500">
                    Used as a contact detail only.
                  </span>
                }
              >
                <Input
                  prefix={<MailOutlined className="text-ink-400" />}
                  placeholder="anita@example.com"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label={<span className="text-sm font-medium text-ink-700">Password</span>}
            name="password"
            rules={[
              { required: true, message: 'password is required' },
              {
                validator: (_r, v) => {
                  const s = passwordStrength(v || '');
                  return s.ok
                    ? Promise.resolve()
                    : Promise.reject(new Error('needs ' + s.reasons.join(', ')));
                }
              }
            ]}
            extra={
              <span className="text-xs text-ink-500">
                At least 12 chars with upper, lower, digit, symbol.
              </span>
            }
          >
            <Input.Password prefix={<LockOutlined className="text-ink-400" />} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Stage 5L: user details rendered in a popup modal. Opens on
          row click / View; close button returns to the table. Same
          panel + same actions as before — the surface is just a Modal
          instead of an inline section, so the page stays focused on
          the table when no user is being inspected. */}
      <Modal
        open={detailsOpen && Boolean(selectedUser)}
        onCancel={closeDetails}
        footer={null}
        width={820}
        destroyOnHidden
        title={null}
        closable
        styles={{ body: { padding: 0 }, content: { borderRadius: 16 } }}
        className="user-details-modal"
      >
        <UserDetailsPanel
          user={selectedUser}
          currentUserId={me ? me.userId || me.id : null}
          onChanged={() => refreshAll()}
          onLock={openLockModal}
          onUnlock={openUnlockModal}
          onSurrender={openSurrenderModal}
        />
      </Modal>
    </div>
  );
}
